import {
  FileProtectOutlined,
  FileTextOutlined,
  TableOutlined,
} from '@ant-design/icons'
import { Tabs } from 'antd'
import React, { FunctionComponent, useCallback, useMemo } from 'react'
import { useRouteMatch } from 'react-router-dom'

import { RESTRICTION_CHECK_LEVEL } from '../../common/epoTypes'
import { useCuzkEpoDetailQuery } from '../../graphql/generated'
import { CuzkEpoDetailTabBarExtraContent } from '../components/CuzkEpoDetailTabBarExtraContent/CuzkEpoDetailTabBarExtraContent'
import { CuzkEpoDetailTabLabel } from '../components/CuzkEpoDetailTabLabel/CuzkEpoDetailTabLabel'
import { CuzkEpoDetailTable } from '../components/CuzkEpoDetailTable/CuzkEpoDetailTable'
import { CuzkEpoHtmlDocumentTab } from '../components/CuzkEpoHtmlDocumentTab/CuzkEpoHtmlDocumentTab'
import { CuzkEpoIdentificationPanel } from '../components/CuzkEpoIdentificationPanel/CuzkEpoIdentificationPanel'
import { CuzkEpoRestrictionCheckInfoPanel } from '../components/CuzkEpoRestrictionCheckInfoPanel/CuzkEpoRestrictionCheckInfoPanel'
import { CuzkEpoRestrictionCheckInProgress } from '../components/CuzkEpoRestrictionCheckInProgress/CuzkEpoRestrictionCheckInProgress'
import { CuzkEpoRestrictionTable } from '../components/CuzkEpoRestrictionTable/CuzkEpoRestrictionTable'
import { LvDetailDrawer } from '../components/LvDetailDrawer/LvDetailDrawer'
import { PageLayout } from '../components/PageLayout/PageLayout'
import { PageSkeleton } from '../components/PageSkeleton/PageSkeleton'
import { isHugeSubjectLimit } from '../utils/layoutConst'
import { COLLECTION_TAB_KEYS, TabItem, tabsProps } from '../utils/tabsHelpers'
import { useEpoRestrictionCheckboxData } from '../utils/useEpoRestrictionCheckboxData'
import { useEpoRestrictionTableData } from '../utils/useEpoRestrictionTableData'
import { useTabKeyState } from '../utils/useTabKeyState'

export const CuzkEpoDetail: FunctionComponent = () => {
  const {
    params: { id },
  } = useRouteMatch<{ id: string }>()

  const [activeTabKey, setActiveTabKey] = useTabKeyState<COLLECTION_TAB_KEYS>(
    COLLECTION_TAB_KEYS.TABLE
  )
  const [queryResult] = useCuzkEpoDetailQuery({
    variables: {
      id,
    },
  })

  const { epoData, lvCount, isHugeSubject } = useMemo(() => {
    const epoData = queryResult.data?.cuzkEpoById
    const lvCount = epoData?.pocetLv || 0
    const isHugeSubject = lvCount > isHugeSubjectLimit
    return { epoData, lvCount, isHugeSubject }
  }, [queryResult.data])

  const tableData = useEpoRestrictionTableData(id)
  const checkboxData = useEpoRestrictionCheckboxData(
    id,
    RESTRICTION_CHECK_LEVEL.LIEN
  )
  const checkboxHugeData = useEpoRestrictionCheckboxData(
    id,
    RESTRICTION_CHECK_LEVEL.RESTRICTION,
    !isHugeSubject
  )

  const refetchData = useCallback(() => {
    checkboxData.refetch()
    checkboxHugeData.refetch()
    tableData.refetch()
  }, [checkboxData, checkboxHugeData, tableData])

  const tabItems = useMemo<TabItem<COLLECTION_TAB_KEYS>[]>(
    () => [
      {
        key: COLLECTION_TAB_KEYS.TABLE,
        label: (
          <CuzkEpoDetailTabLabel
            icon={<TableOutlined />}
            title="Zobrazení v tabulce"
          />
        ),
        disabled: !lvCount,
        children: (
          <div className="bg-white p-6">
            <CuzkEpoIdentificationPanel
              loading={queryResult.fetching}
              subjectData={epoData}
            />
            <CuzkEpoDetailTable
              epoId={id}
              lvCount={lvCount}
              isHugeSubject={isHugeSubject}
            />
          </div>
        ),
      },
      {
        key: COLLECTION_TAB_KEYS.DOCUMENT,
        label: (
          <CuzkEpoDetailTabLabel
            icon={<FileTextOutlined />}
            title="Zobrazení v listině"
            isHugeSubject={isHugeSubject}
          />
        ),
        children: <CuzkEpoHtmlDocumentTab epoId={id} />,
        disabled: isHugeSubject,
      },

      {
        key: COLLECTION_TAB_KEYS.RESTRICTIONS,
        label: (
          <CuzkEpoDetailTabLabel
            icon={<FileProtectOutlined />}
            title="Zobrazení práv 3. stran (exekuce, insolvence...)"
          />
        ),
        disabled: !lvCount,
        children: (
          <div className="bg-white p-6  ">
            <CuzkEpoRestrictionCheckInfoPanel
              epoId={id}
              subjectData={epoData}
              refetch={refetchData}
              checkboxHugeData={checkboxHugeData}
              checkboxData={checkboxData}
            />
            <CuzkEpoRestrictionCheckInProgress
              epoId={id}
              refetch={refetchData}
              checkboxData={checkboxData}
            />
            <CuzkEpoRestrictionTable
              epoId={id}
              refetch={refetchData}
              isHugeSubject={isHugeSubject}
              dataSource={tableData}
              checkboxData={isHugeSubject ? checkboxHugeData : checkboxData}
            />
          </div>
        ),
      },
    ],
    [
      lvCount,
      queryResult.fetching,
      epoData,
      id,
      isHugeSubject,
      refetchData,
      checkboxHugeData,
      checkboxData,
      tableData,
    ]
  )

  if (queryResult.fetching && !epoData) {
    return <PageSkeleton />
  }
  return (
    <>
      <PageLayout
        title={`Evidence práv pro ${epoData?.nazev || 'osobu'}`}
        isContainer
      >
        <Tabs
          {...tabsProps}
          items={tabItems}
          onTabClick={setActiveTabKey}
          activeKey={lvCount ? activeTabKey : COLLECTION_TAB_KEYS.DOCUMENT}
          tabBarExtraContent={
            <CuzkEpoDetailTabBarExtraContent
              id={id}
              isHugeSubject={isHugeSubject}
              format={epoData?.format}
            />
          }
        />
      </PageLayout>
      <LvDetailDrawer />
    </>
  )
}

export default CuzkEpoDetail
